/* eslint-disable @next/next/no-img-element */
import { CalendarDays, Copy, MessageSquare } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { format } from 'date-fns';
// import ActionMini from '../../public/icons/suggested-action-mini.svg';
// import ActionEmpty from '../../public/icons/suggested-action.svg';
import { SHOW_SNACKBAR } from '../../store/types';
import { Skeleton } from '../ui/skeleton';
import CustomerService from '../../services/customer/customer.service';

type Props = {
  customerId: number;
  isDataLoading: boolean;
  nextSteps: {[key: string]: any}[];
  actions: {[key: string]: string};
  showTalkingPoints?: boolean;
  fetchCustomerViewData: (customerId: number) => void
  fetchCustomerSuggestedActions: (customerId: number) => void
}

const CustomerViewRightPanel = ({ 
  customerId, 
  isDataLoading, 
  nextSteps, 
  actions, 
  showTalkingPoints = true,
  fetchCustomerViewData,
  fetchCustomerSuggestedActions
}: Props) => {
    const userData: any =
        localStorage && JSON.parse(localStorage.getItem('user_data'));
  const dispatch = useDispatch();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    dispatch({
      type: SHOW_SNACKBAR,
      payload: {
        type: 'success',
        message: 'Copied to clipboard',
      },
    });
  };

  const updateFeedback = async (step) => {
    const payload = {
      is_completed: !step?.completed_at
    }
    if(userData?.id === step?.user_id){
      await CustomerService.updateActionItems(customerId, step?.id, payload);
      await fetchCustomerViewData(customerId);
      await fetchCustomerSuggestedActions(customerId);
    } else {
      dispatch({
      type: SHOW_SNACKBAR,
      payload: {
        type: 'error',
        message: 'This task is assigned to someone else!',
      },
    });
    }
  }

  if (isDataLoading && (!nextSteps?.length && !actions?.pitch_improvements)) {
    return (
      <div className='w-full h-full overflow-y-auto relative p-4'>
        <div className='border border-[#D7D7D7] p-3 rounded-lg'>
          {
            new Array(2).fill(null).map((_, idx) => (
              <>
                <div className='flex flex-col gap-2.5 w-full'>
                  <div className='flex gap-2.5 items-start'>
                    <Skeleton className='w-4 h-4 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
                    <div className='flex flex-col gap-2 w-full'>
                      <Skeleton className='w-[200px] h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
                      <Skeleton className='w-full h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
                      <Skeleton className='w-[230px] h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
                    </div>
                  </div>
                  <div className='flex justify-end items-center gap-2'>
                    <Skeleton className='w-[100px] h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
                  </div>
                </div>
                {idx !== 1 && <div className='border-t border-[#D7D7D7] my-5'/>}
              </>
            ))
          }
        </div>
        <div className='border border-[#D7D7D7] p-3 rounded-lg mt- flex flex-col gap-2'>
          <Skeleton className='w-[200px] h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7] mb-2'/>
          <Skeleton className='w-full h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
          <Skeleton className='w-full h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
          <Skeleton className='w-full h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]'/>
          <Skeleton className='w-[230px] h-5 rounded bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7] mt-2'/>
        </div>
      </div>
    )
  }
  
  return (
    <div className="w-full h-full relative flex flex-col gap-3 p-3 pr-0 lg:p-4 lg:pr-0 lg:overflow-y-auto">
      <div 
        className={`border border-[#D7D7D7] rounded-lg p-3 mr-3 flex flex-col gap-3 lg:gap-5 ${
          nextSteps?.length ? '' : 'h-full min-h-[230px]'
        }`}
      >
        <div className='flex gap-1.5 items-center'>
          {Boolean(nextSteps?.length) && <img className='w-6 h-6' src="/icons/suggested-action-mini.svg" alt="icon" />}
          <p className='text-sm font-bold'>Suggested Actions</p>
        </div>
        {nextSteps?.length ? (
          <div className='flex flex-col gap-3 lg:gap-5'>
            {nextSteps.map((step, idx) => (
              <div className='flex flex-col gap-2' key={`${step?.label || ''}_${idx}`}>
                <div className='flex gap-2.5 items-start'>
                  <input
                    checked={Boolean(step?.completed_at)}
                    name="setup-followup-meeting"
                    type="checkbox"
                    className="focus:ring-0 w-4 h-4 rounded-[2px] border border-[#D7D7D7] 
                    text-positive-green appearance-none cursor-pointer focus:ring-offset-0 mt-[2px]"
                    onChange={() => updateFeedback(step)}
                  />
                  <div className='flex flex-col gap-1 text-sm leading-5'>
                    <p className='font-bold'>{step?.label}</p>
                    <p className='font-normal'>{step?.description}</p>
                  </div>
                </div>
                {(step?.due_date || step?.due_date_str) && (
                  <div className='flex justify-end items-center gap-2'>
                    <p className="text-secondary text-xs leading-4 font-medium">
                      Due on: {step?.due_date_str ? step?.due_date_str : format(new Date(step?.due_date), 'MMM dd yyyy, hh:mm a')}
                    </p>
                    <CalendarDays className="w-4 h-4 text-secondary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center gap-4 h-full'>
            <img className='w-[52px] h-[52px]' src="/icons/suggested-action.svg" alt="icon" />
            <p className='text-xs lg:text-sm text-center leading-5'>
              You&apos;ll get a list of next steps as you do more conversations with this customer.
            </p>
          </div>
        )}
      </div>
      {showTalkingPoints && (
        <div 
          className={`border border-[#D7D7D7] rounded-lg p-3 mr-3 flex flex-col gap-3 lg:gap-5 ${
            actions?.pitch_improvements ? '' : 'h-full min-h-[230px]'
          }`}
        >
          <div className='flex gap-1.5 items-center'>
            {actions?.pitch_improvements && <MessageSquare className='text-[#29B1E9] w-6 h-6'/>}
            <p className='text-sm font-bold'>Talking Points for Next Conversation</p>
          </div>
          {actions?.pitch_improvements || 
          (Array.isArray(actions?.pitch_improvements) && actions?.pitch_improvements?.length) ? (
            <div className='flex flex-col gap-2 text-xs lg:text-sm leading-5'>
              {Array.isArray(actions?.pitch_improvements) ? (
                <>
                  {actions?.pitch_improvements.map((itm, idx) => (
                    <p className='font-normal' key={idx}>{itm}</p>
                  ))}
                </>
              ) : (
                <p className='font-normal'>{actions?.pitch_improvements}</p>
              )}
              <div className='flex justify-end items-center gap-2 mt-4'>
                <button
                  type='button'
                  className="text-secondary text-xs leading-4 font-semibold flex justify-center items-center gap-1"
                  onClick={() => copyToClipboard(actions?.pitch_improvements)}
                >
                  <Copy className="w-3 h-3" />
                  Copy
                </button>
              </div>
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center gap-4 h-full'>
              <MessageSquare className='text-[#29B1E9] w-10 h-10'/>
              <p className='text-xs lg:text-sm text-center leading-5'>
                Talking points for your next conversation with this customer will be listed here.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CustomerViewRightPanel